import { Save } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContentTitle,
  DialogForm,
  DialogTitle,
} from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { DeviceMessagingBackend } from '~/generated/graphql';
import { capitalize } from '~/lib/string';
import type {
  DeviceList__Channel as Channel,
  DeviceList__DeviceGroup as DeviceGroup,
  DeviceList__TvBrand as TVBrand,
} from '../../queries/list.generated';
import { SCREENSHOT_INTERVALS } from '../lib/helpers';
import type { DeviceBulkPatchInput } from './BulkSettingsDialog';

interface BulkUpdateConfirmDialogProps {
  channels: ReadonlyArray<Channel>;
  deviceGroups: ReadonlyArray<DeviceGroup>;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
  patch: DeviceBulkPatchInput;
  selectedDeviceCount: number;
  tvBrands: ReadonlyArray<TVBrand>;
}

interface PatchEntryProps {
  label: string;
  value: string;
}

const PatchEntry = ({ label, value }: PatchEntryProps) => (
  <Box mb={2}>
    <label>
      <span className="label" style={{ fontWeight: 'bold' }}>
        {label}:{' '}
      </span>
      <span>{value}</span>
    </label>
  </Box>
);

export const BulkUpdateConfirmDialog = ({
  channels,
  deviceGroups,
  loading,
  onConfirm,
  onCancel,
  open,
  patch,
  selectedDeviceCount,
  tvBrands,
}: BulkUpdateConfirmDialogProps) => {
  const { t } = useTranslation();

  const channel = useMemo(
    () => channels.find((c) => c.id === patch.defaultChannelId),
    [channels, patch],
  );

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onCancel}>
        {t('pages.devices.bulk_actions.settings.confirm_title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentTitle>
          {selectedDeviceCount > 0
            ? t('pages.devices.bulk_actions.settings.confirm_description', {
                count: selectedDeviceCount,
                thing: t('common.pluralize.device_plural', { count: selectedDeviceCount }),
              })
            : t('pages.devices.bulk_actions.settings.confirm_description_all', {
                thing: t('common.pluralize.device_plural', { count: 2 }),
              })}
        </DialogContentTitle>
        <DialogForm>
          {patch.screenshotInterval !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.automatic_screenshots')}
              value={
                patch.screenshotInterval === null
                  ? t('pages.devices.bulk_actions.settings.disable')
                  : SCREENSHOT_INTERVALS[patch.screenshotInterval] ||
                    t('pages.devices.bulk_actions.settings.unknown')
              }
            />
          )}

          {patch.api !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.api')}
              value={
                patch.api
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.defaultChannelId !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.default_channel')}
              value={
                channel
                  ? `${channel.name}${channel.number ? ` (${channel.number})` : ''}`
                  : t('pages.devices.bulk_actions.settings.no_default_channel')
              }
            />
          )}

          {patch.demo !== undefined && (
            <PatchEntry
              label={t('common.labels.demo')}
              value={
                patch.demo
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.deviceGroupId !== undefined && (
            <PatchEntry
              label={t('common.labels.device_group')}
              value={
                deviceGroups.find((g) => g.id === patch.deviceGroupId)?.name ??
                t('pages.devices.bulk_actions.settings.no_device_group')
              }
            />
          )}

          {patch.eventStream !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.event_stream')}
              value={
                patch.eventStream
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.internal !== undefined && (
            <PatchEntry
              label={t('common.labels.internal')}
              value={
                patch.internal
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.nightlyBehavior !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.nightly_behavior')}
              value={capitalize(patch.nightlyBehavior)}
            />
          )}

          {patch.overscan !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.overscan')}
              value={`${patch.overscan}%`}
            />
          )}

          {patch.preload !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.preload')}
              value={
                patch.preload
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.realtime !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.realtime')}
              value={
                patch.realtime
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.messagingBackend !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.messaging_backend')}
              value={
                patch.messagingBackend === DeviceMessagingBackend.Pubnub
                  ? 'PubNub'
                  : patch.messagingBackend === DeviceMessagingBackend.Mqtt
                  ? 'MQTT (Dev Only)'
                  : 'IoT Core'
              }
            />
          )}

          {patch.silence !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.silence')}
              value={
                patch.silence
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.snmpService !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.snmp_service')}
              value={
                patch.snmpService
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.tvBrandId !== undefined && (
            <PatchEntry
              label={t('common.labels.tv_brand')}
              value={
                tvBrands.find((b) => b.id === patch.tvBrandId)?.name ??
                t('pages.devices.bulk_actions.settings.no_tv_brand')
              }
            />
          )}

          {patch.cacheContent !== undefined && (
            <PatchEntry
              label={t('pages.devices.bulk_actions.settings.cache_content')}
              value={
                patch.cacheContent
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}

          {patch.closedCaptioning !== undefined && (
            <PatchEntry
              label={t('common.device_form_labels.closed_captioning')}
              value={
                patch.closedCaptioning
                  ? t('pages.devices.bulk_actions.settings.yes')
                  : t('pages.devices.bulk_actions.settings.no')
              }
            />
          )}
        </DialogForm>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          {t('common.dialogs.cancel')}
        </Button>
        <LoadingButton
          loadingPosition="start"
          variant="contained"
          color="primary"
          loading={loading}
          startIcon={<Save />}
          onClick={onConfirm}
        >
          {t('pages.devices.bulk_actions.settings.apply_changes')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
